import React from 'react';
import { estadoDe } from '../../utils/estados';

const variants = {
  default: {
    background: 'var(--bg-tertiary)',
    color: 'var(--text)',
    border: '1px solid var(--border)',
  },
  primary: {
    background: 'var(--gold-subtle)',
    color: 'var(--gold-dark)',
    border: '1px solid var(--gold)',
  },
  secondary: {
    background: 'var(--midnight-10)',
    color: 'var(--midnight)',
    border: '1px solid var(--midnight-20)',
  },
  success: {
    background: 'var(--success-bg)',
    color: 'var(--success-text)',
    border: '1px solid var(--success)',
  },
  warning: {
    background: 'var(--warning-bg)',
    color: 'var(--warning-text)',
    border: '1px solid var(--warning)',
  },
  danger: {
    background: 'var(--danger-bg)',
    color: 'var(--danger-text)',
    border: '1px solid var(--danger)',
  },
  info: {
    background: 'var(--info-bg)',
    color: 'var(--info-text)',
    border: '1px solid var(--info)',
  },
  luxury: {
    background: 'var(--gradient-gold)',
    color: 'white', // Always white text on gold badges
    border: 'none',
    fontWeight: 600,
  },
  // Status variants for backwards compatibility
  pending: {
    background: 'var(--warning-bg)',
    color: 'var(--warning-text)',
    border: '1px solid var(--warning)',
  },
  entered: {
    background: 'var(--success-bg)',
    color: 'var(--success-text)',
    border: '1px solid var(--success)',
  },
  exited: {
    background: 'var(--info-bg)',
    color: 'var(--info-text)',
    border: '1px solid var(--info)',
  },
};

const sizes = {
  sm: {
    padding: '0.25rem 0.75rem',
    fontSize: 'var(--text-xs)',
    borderRadius: 'var(--radius-full)',
  },
  md: {
    padding: '0.375rem 1rem',
    fontSize: 'var(--text-sm)',
    borderRadius: 'var(--radius-full)',
  },
  lg: {
    padding: '0.5rem 1.25rem',
    fontSize: 'var(--text-base)',
    borderRadius: 'var(--radius-full)',
  },
};

const styles = {
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontFamily: 'var(--font-body)',
    fontWeight: 500,
    letterSpacing: 'var(--tracking-wide)',
    textTransform: 'uppercase',
    transition: 'all 0.2s ease',
    position: 'relative',
    overflow: 'hidden',
    lineHeight: 1,
  },
  dot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: 'currentColor',
    opacity: 0.8,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  shimmer: {
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)',
    animation: 'shimmer 2s infinite',
  },
};

export function Badge({
  children, 
  variant = 'default',
  size = 'md',
  dot = false,
  icon,
  shimmer = false,
  style = {},
  className = '',
  ...props 
}) {
  const variantStyle = variants[variant] || variants.default;
  const sizeStyle = sizes[size] || sizes.md;
  
  const iconSize = {
    sm: 12,
    md: 14,
    lg: 16,
  }[size];

  return (
    <span
      className={`luxury-badge ${className}`}
      style={{
        ...styles.badge,
        ...variantStyle,
        ...sizeStyle,
        ...style,
      }}
      {...props}
    >
      {dot && !icon && <span style={styles.dot} />}
      
      {icon && (
        <span style={styles.iconWrapper}>
          {typeof icon === 'string' ? (
            <span>{icon}</span>
          ) : React.isValidElement(icon) ? (
            icon
          ) : (
            React.createElement(icon, { size: iconSize })
          )}
        </span>
      )}
      
      {children}
      
      {shimmer && variant === 'luxury' && (
        <div style={styles.shimmer} />
      )}
    </span>
  );
}

/**
 * La insignia de estado de una visita — los SIETE, no tres.
 *
 * La tabla con los siete estados y su respaldo vive en `utils/estados.js`, no
 * aquí. Estaba repetida: esta insignia decía «Revocada» mientras la búsqueda
 * del guardia decía «Revocado» y el historial enseñaba `Revoked` crudo, así
 * que el mismo estado tenía tres nombres según la pantalla. `estadoDe` también
 * es quien decide que un estado desconocido se enseñe tal cual en vez de caer
 * a «Pendiente», que es lo que hacía que un pase revocado se viera idéntico a
 * uno válido.
 */
export function StatusBadge({ status, size = 'md', ...props }) {
  const { variant, icono, etiqueta } = estadoDe(status);

  return (
    <Badge variant={variant} icon={icono} size={size} {...props}>
      {etiqueta}
    </Badge>
  );
}

export default Badge;